import { Authenticator as AmplifyAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signInWithRedirect,
} from 'aws-amplify/auth';
import log from 'loglevel';
import { ReactNode, useEffect, useState } from 'react';
import { UserContext } from '../context/userContext';
import { User } from '@/types';
import { Box, CircularProgress } from '@mui/material';

interface AuthenticatorProps {
  customProvider?: string;
  children: ReactNode;
}

export const isUserInGroup = (
  user: User | null,
  groupToCheck: string | string[]
) => {
  if (!user) return false;
  if (Array.isArray(groupToCheck)) {
    return groupToCheck.some(group => user.cognitoGroups.includes(group));
  }
  return user.cognitoGroups.includes(groupToCheck);
};

function EricksonAuthenticator({
  customProvider,
  children,
}: AuthenticatorProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    async function checkUserAuthentication() {
      try {
        // Will throw if the user is not signed in
        await getCurrentUser();
        // If getCurrentUser succeeds, ensure we can also fetch a valid session:
        await fetchAuthSession();
        setIsAuthenticated(true);
      } catch (error) {
        // If any step fails, consider the user not authenticated
        setIsAuthenticated(false);
        log.debug('Error checking user authentication:', error);

        // Store the current path before redirecting
        sessionStorage.setItem('redirectPath', window.location.pathname);

        // If there's a custom provider, redirect to it for sign-in:
        if (customProvider) {
          log.debug('Custom auth flow: redirecting to provider');
          try {
            await signInWithRedirect({ provider: { custom: customProvider } });
          } catch (error) {
            log.error('Error signing in with custom provider:', error);
          }
        } else {
          log.debug('Dev auth flow: no custom provider');
          // Fall back to the <AmplifyAuthenticator>.
        }
      }
    }
    void checkUserAuthentication();
  }, [customProvider]);

  useEffect(() => {
    // Only fetch user attributes if the user is authenticated
    if (isAuthenticated) {
      async function loadUserDetails() {
        try {
          const userAttributes = await fetchUserAttributes();
          const session = await fetchAuthSession();

          // Extract Cognito groups from the session tokens
          const accessToken = session.tokens?.accessToken;
          let cognitoGroups: string[] = [];

          if (
            accessToken &&
            typeof accessToken.payload === 'object' &&
            accessToken.payload !== null
          ) {
            const groups = accessToken.payload['cognito:groups'];
            if (Array.isArray(groups)) {
              cognitoGroups = groups as string[];
            }
          }

          const currentTime = new Date();
          const _user: User = {
            userId: userAttributes.sub ?? '',
            email: userAttributes.email ?? '',
            loginTime: currentTime,
            cognitoGroups,
            // TODO: do something similar for other roles
            hasFileManagerAccess:
              cognitoGroups.includes('FileManagerAdmin') ||
              cognitoGroups.includes('Admin'),
          };

          setUser(_user);
        } catch (error) {
          log.error('Error fetching user attributes or session:', error);
        }
      }

      void loadUserDetails();
    }
  }, [isAuthenticated]);

  if (isAuthenticated === null) {
    // Show a loading spinner while providing context with loading state
    return (
      <UserContext.Provider
        value={{ user, setUser, setIsAuthenticated, isAuthenticated }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            width: '100%',
          }}
        >
          <CircularProgress size={100} />
        </Box>
      </UserContext.Provider>
    );
  }

  if (isAuthenticated) {
    log.debug('User is authenticated');
    return (
      <UserContext.Provider
        value={{ user, setUser, setIsAuthenticated, isAuthenticated }}
      >
        {children}
      </UserContext.Provider>
    );
  } else {
    if (customProvider) {
      // If customProvider is set, we've already tried to redirect in the effect
      log.debug('Using custom IDP - returning null');
      return (
        <UserContext.Provider
          value={{ user, setUser, setIsAuthenticated, isAuthenticated }}
        >
          {null}
        </UserContext.Provider>
      );
    } else {
      // If no custom provider, fall back to the AmplifyAuthenticator UI
      log.debug('Using dev IDP - rendering default Amplify Authenticator');
      return (
        <UserContext.Provider
          value={{ user, setUser, setIsAuthenticated, isAuthenticated }}
        >
          <AmplifyAuthenticator>{children}</AmplifyAuthenticator>
        </UserContext.Provider>
      );
    }
  }
}

export default EricksonAuthenticator;
